import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Camera, Film, Video, Edit, Plane as Drone, Presentation } from 'lucide-react';
import { MEDIA_CONFIG } from '../data/media';
import ContactButton from '../components/ContactButton';

const SERVICES = [
  {
    icon: Film,
    title: "Filmproduktion",
    description: "Individuelle Filmproduktionen von der ersten Idee bis zum finalen Schnitt.",
    details: [
      "Imagefilme & Unternehmensfilme",
      "Werbefilme & Commercials",
      "Dokumentationen",
      "Produktfilme"
    ],
    expandedContent: {
      image: `/media/images/services/filmproduction.jpeg`,
      description: `Die Filmproduktion ist meine Leidenschaft. Mit modernster Technik und jahrelanger Erfahrung 
      erschaffe ich hochwertige Filme, die Ihre Botschaft perfekt transportieren. Von der ersten Konzeptbesprechung 
      bis zum finalen Schnitt begleite ich Sie persönlich durch den gesamten Produktionsprozess. Dabei setze ich auf 
      eine enge Zusammenarbeit, um Ihre Vision optimal umzusetzen.

      Meine Filmproduktionen zeichnen sich durch cinematographische Bildsprache, durchdachte Dramaturgie und 
      technische Perfektion aus. Ob Imagefilm, Werbespot oder Dokumentation – ich finde die passende visuelle 
      Sprache für Ihr Projekt.`
    }
  },
  {
    icon: Video,
    title: "Videografie",
    description: "Kreative Videoproduktionen für Social Media und digitales Marketing.",
    details: [
      "Social Media Content",
      "Event-Videografie",
      "Produktvideos",
      "Tutorial- & Erklärvideos"
    ],
    expandedContent: {
      image: `/media/images/services/videography.jpeg`,
      description: `Die Videografie für Social Media und digitales Marketing erfordert ein besonderes Gespür für 
      zeitgemäße Formate und Trends. Ich produziere maßgeschneiderte Videoinhalte, die Ihre Zielgruppe dort 
      erreichen, wo sie aktiv ist. Dabei achte ich besonders auf die spezifischen Anforderungen der verschiedenen 
      Plattformen.

      Von dynamischen Instagram Reels über professionelle YouTube-Videos bis hin zu viralen TikTok-Clips – ich 
      verstehe die Besonderheiten jeder Plattform und setze Ihre Botschaft optimal in Szene.`
    }
  },
  {
    icon: Edit,
    title: "Postproduktion",
    description: "Professionelle Nachbearbeitung Ihrer Medien mit modernster Technik.",
    details: [
      "Schnitt & Montage",
      "Farbkorrektur & Grading",
      "Visual Effects",
      "Sounddesign & Audio-Mastering"
    ],
    expandedContent: {
      image: `/media/images/services/postproduction.jpeg`,
      description: `In der Postproduktion erwecke ich Ihr Filmmaterial zum Leben. Mit modernster Software und 
      kreativer Expertise verwandle ich Rohmaterial in fesselnde Geschichten. Vom präzisen Schnitt über 
      atmosphärisches Color Grading bis hin zu professionellem Sounddesign – ich optimiere jeden Aspekt Ihres 
      Projekts.

      Meine Postproduktion umfasst auch die Integration von Visual Effects und Motion Graphics, um Ihre Botschaft 
      noch eindrucksvoller zu gestalten. Das Ergebnis sind technisch perfekte und emotional packende Filme.`
    }
  },
  {
    icon: Camera,
    title: "Fotografie",
    description: "Hochwertige Fotografie für Ihr Unternehmen und Events.",
    details: [
      "Businessfotografie",
      "Portraitfotografie",
      "Event-Fotografie",
      "Reportagefotografie"
    ],
    expandedContent: {
      image: `/media/images/services/photography.jpeg`,
      description: `Professionelle Fotografie ist die Kunst, Momente einzufangen und Geschichten in einem einzigen 
      Bild zu erzählen. Mit meinem fotografischen Know-how schaffe ich ausdrucksstarke Bilder, die Ihr 
      Unternehmen von seiner besten Seite zeigen. Von Corporate Portraits über Produktfotografie bis hin zur 
      Dokumentation von Events – ich liefere Bildmaterial in höchster Qualität.

      Dabei lege ich besonderen Wert auf eine natürliche, authentische Bildsprache, die Ihre Unternehmensidentität 
      optimal transportiert.`
    }
  },
  {
    icon: Drone,
    title: "Drohnenaufnahmen",
    description: "Spektakuläre Luftaufnahmen für einzigartige Perspektiven.",
    details: [
      "Luftaufnahmen",
      "360° Panoramen",
      "Gelände-Dokumentation",
      "Event-Dokumentation"
    ],
    expandedContent: {
      image: `/media/images/services/drone.jpeg`,
      description: `Drohnenaufnahmen eröffnen völlig neue Perspektiven und verleihen Ihrem Projekt eine zusätzliche 
      Dimension. Mit modernster Drohnentechnik und allen erforderlichen Lizenzen realisiere ich atemberaubende 
      Luftaufnahmen. Ob dynamische Flüge über Ihr Firmengelände, spektakuläre Architekturaufnahmen oder 
      eindrucksvolle Event-Dokumentationen – ich hebe Ihre Visualisierung auf ein neues Level.

      Dabei achte ich stets auf höchste Sicherheitsstandards und die Einhaltung aller rechtlichen Vorgaben.`
    }
  },
  {
    icon: Presentation,
    title: "Beratung & Schulung",
    description: "Strategische Beratung und Workshops für Ihre Medienprojekte.",
    details: [
      "Konzeptentwicklung",
      "Content-Strategie",
      "Medienberatung",
      "Workshops & Schulungen"
    ],
    expandedContent: {
      image: `/media/images/services/consulting.jpeg`,
      description: `Meine Beratungs- und Schulungsangebote helfen Ihnen, das volle Potenzial Ihrer Medienprojekte 
      auszuschöpfen. Mit fundiertem Fachwissen und praktischer Erfahrung unterstütze ich Sie bei der Entwicklung 
      erfolgreicher Content-Strategien und der optimalen Nutzung verschiedener Medienkanäle.

      In individuellen Workshops und Schulungen vermittle ich praktisches Know-how und zeige Ihnen, wie Sie Ihre 
      Medienproduktion professionell gestalten können.`
    }
  }
];

const Services = () => {
  const [selectedService, setSelectedService] = useState<number | null>(null);
  const [navbarHeight, setNavbarHeight] = useState(80);
  const serviceRefs = useRef<(HTMLDivElement | null)[]>([]);
  const contentRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const updateNavbarHeight = () => {
      const navbar = document.querySelector('nav');
      if (navbar) {
        setNavbarHeight(navbar.offsetHeight);
      }
    };

    updateNavbarHeight();
    window.addEventListener('resize', updateNavbarHeight);
    return () => window.removeEventListener('resize', updateNavbarHeight);
  }, []);

  const handleServiceClick = (index: number) => {
    if (selectedService === index) {
      setSelectedService(null);
    } else {
      setSelectedService(index);
      
      // Wait for content to be rendered before scrolling
      requestAnimationFrame(() => {
        const serviceElement = serviceRefs.current[index];
        if (serviceElement) {
          const yOffset = -navbarHeight - 20;
          const y = serviceElement.getBoundingClientRect().top + window.pageYOffset + yOffset;
          
          window.scrollTo({
            top: y,
            behavior: 'smooth'
          });
        }
      });
    }
  };

  const ServiceCard = ({ service, index, isSecondRow = false }: {
    service: typeof SERVICES[0],
    index: number,
    isSecondRow?: boolean
  }) => {
    const actualIndex = isSecondRow ? index + 3 : index;

    return (
      <motion.div
        ref={el => serviceRefs.current[actualIndex] = el}
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
        className="relative"
        layout
      >
        <motion.div
          className={`bg-zinc-900 rounded-lg p-8 cursor-pointer transition-all ${
            selectedService === actualIndex ? 'bg-zinc-800' : 'hover:bg-zinc-800'
          }`}
          onClick={() => handleServiceClick(actualIndex)}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          layout
        >
          <motion.div layout className="flex items-center mb-6">
            <service.icon size={32} className="text-white" />
            <h3 className="text-2xl font-semibold ml-4">{service.title}</h3>
          </motion.div>
          <motion.p layout className="text-gray-400 mb-6">{service.description}</motion.p>
          <motion.ul layout className="space-y-2">
            {service.details.map((detail, idx) => (
              <motion.li key={idx} className="flex items-center" layout>
                <div className="w-1.5 h-1.5 bg-white rounded-full mr-3"></div>
                {detail}
              </motion.li>
            ))}
          </motion.ul>
        </motion.div>

        <AnimatePresence>
          {selectedService === actualIndex && (
            <motion.div
              ref={el => contentRefs.current[actualIndex] = el}
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
              className="mt-4 bg-zinc-800 rounded-lg overflow-hidden md:hidden"
              layout
            >
              <motion.div layout>
                <img
                  src={service.expandedContent.image}
                  alt={service.title}
                  className="w-full aspect-video object-cover"
                />
                <div className="p-6">
                  <p className="text-gray-300 whitespace-pre-line">
                    {service.expandedContent.description}
                  </p>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    );
  };

  const ExpandedContent = ({
    service,
    className
  }: {
    service: typeof SERVICES[0],
    className: string
  }) => (
    <motion.div
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: "auto" }}
      exit={{ opacity: 0, height: 0 }}
      transition={{ duration: 0.3, ease: "easeInOut" }}
      className={`hidden md:block ${className} bg-zinc-800 rounded-lg overflow-hidden`}
      layout
    >
      <motion.div className="grid md:grid-cols-2 min-h-[400px]" layout>
        <motion.div className="flex items-center" layout>
          <div className="w-full aspect-video">
            <img
              src={service.expandedContent.image}
              alt={service.title}
              className="w-full h-full object-cover"
            />
          </div>
        </motion.div>
        <motion.div className="p-8 flex items-center" layout>
          <p className="text-gray-300 whitespace-pre-line">
            {service.expandedContent.description}
          </p>
        </motion.div>
      </motion.div>
    </motion.div>
  );

  const firstRow = SERVICES.slice(0, 3);
  const secondRow = SERVICES.slice(3);

  return (
    <div className="min-h-screen bg-black text-white pt-20">
      <div className="relative h-[40vh] flex items-center justify-center overflow-hidden">
        <img
          src={`${MEDIA_CONFIG.paths.backgrounds}/headers/services-header.jpg`}
          alt="Professionelle Filmproduktion"
          className="absolute w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/60"></div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="relative z-10 text-center px-4"
        >
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Meine Leistungen</h1>
          <p className="text-xl max-w-2xl mx-auto">
            Professionelle Medienproduktion für Ihr Unternehmen
          </p>
        </motion.div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-16 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <p className="text-xl text-gray-300">
            Mit langjähriger Erfahrung und modernster Technik biete ich Ihnen das komplette Spektrum 
            professioneller Medienproduktion. Jedes Projekt wird individuell auf Ihre Bedürfnisse 
            zugeschnitten, um Ihre Botschaft optimal zu transportieren und Ihre Zielgruppe zu erreichen.
          </p>
        </motion.div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-16">
        <motion.div layout className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
          {firstRow.map((service, index) => (
            <ServiceCard key={index} service={service} index={index} />
          ))}
        </motion.div>

        <AnimatePresence>
          {selectedService !== null && selectedService < 3 && (
            <ExpandedContent
              service={firstRow[selectedService]}
              className="mb-8"
            />
          )}
        </AnimatePresence>

        <motion.div layout className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {secondRow.map((service, index) => (
            <ServiceCard
              key={index}
              service={service}
              index={index}
              isSecondRow={true}
            />
          ))}
        </motion.div>

        <AnimatePresence>
          {selectedService !== null && selectedService >= 3 && (
            <ExpandedContent
              service={secondRow[selectedService - 3]}
              className="mt-8"
            />
          )}
        </AnimatePresence>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mt-16 text-center"
        >
          <p className="text-xl text-gray-400 mb-8">
            Lassen Sie uns gemeinsam Ihr nächstes Projekt verwirklichen
          </p>
          <ContactButton />
        </motion.div>
      </div>
    </div>
  );
};

export default Services;